/**
 * legacyCopy entries for instance-subtask errors (`ErroDataType` family) —
 * ported verbatim from `SuggestedActionForInstanceSubtaskError` (howToFixIt)
 * and `getInstanceSubTaskErrorDetails` (whatCausedIt) in
 * `../../ParticipantDialog.tsx`.
 */
import { TemplateResourceType } from '@dadohr/shared/domainTypes/experience';
import { Link } from '@material-ui/core';
import React, { ReactNode } from 'react';
import { UnsafeTFunction } from '../../../../../../i18next';
import { channelMarkerToString } from '../../../../../../services/channels';
import { LightMdToJsx } from '../../../../../../services/formatter';
import { defaultHumanReadableActorName } from '../textHelpers';
import { CopyPack, Issue, IssueKind } from '../types';

const dataOf = (group: Issue[]) => (group[0].raw as { family: 'INSTANCE_SUBTASK'; data: any }).data;
const ctxOf = (group: Issue[]) => group[0].ctx;
const md = (text: string | null): ReactNode => (text ? <LightMdToJsx>{text}</LightMdToJsx> : null);

const humanReadableActorName = (group: Issue[], actorKey: string): string => {
  const c = ctxOf(group);
  return (c.humanReadableActorName || defaultHumanReadableActorName)(actorKey, c.supportingActors);
};

// getBackgroundCheckDetail/BackgroundCheckSuggestedAction live in
// BackgroundCheckInstanceProblems.tsx, which transitively imports
// services/user/index.ts (Vite-only `import.meta`) via services/orgs and
// services/currentUser. Injected via context for the same reason as
// humanReadableActorName above (see IssueCopyContext in ../types.ts).
const getBackgroundCheckDetail = (group: Issue[]): string => {
  const c = ctxOf(group);
  if (!c.getBackgroundCheckDetail) return c.t('Unknown error');
  return c.getBackgroundCheckDetail({ errData: dataOf(group), supportingActors: c.supportingActors || {}, t: c.t });
};

const BackgroundCheckSuggestedAction = (group: Issue[]): ReactNode => {
  const c = ctxOf(group);
  return c.BackgroundCheckSuggestedAction ? c.BackgroundCheckSuggestedAction({ errData: dataOf(group) }) : null;
};

export const instanceSubtaskLegacyCopy: Partial<CopyPack> = {
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL]: {
    category: 'Supporting actor assignment',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return c.t(`Couldn't assign **{{actor}}** - the **Work Email** **{{email}}** is invalid`, {
        actor: humanReadableActorName(g, d.actorKey),
        email: d.workEmail
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL]!.summary(g),
    howToFixIt: (g) => md(ctxOf(g).t(`Please provide a valid email address`))
  },
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND]: {
    category: 'Supporting actor assignment',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return c.t(`Couldn't assign **{{actor}}** - no user found with **Work Email** **{{email}}**`, {
        actor: humanReadableActorName(g, d.actorKey),
        email: d.workEmail
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND]!.summary(
        g
      ),
    howToFixIt: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return md(
        c.t(`Assign different **{{actor}}** or create user with this email address`, {
          actor: humanReadableActorName(g, d.actorKey)
        })
      );
    }
  },
  [IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND]: {
    category: 'Supporting actor assignment',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return c.t(`Supporting actor **{{actor}}** not found for this experience`, {
        actor: humanReadableActorName(g, d.actorKey)
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND]!.summary(
        g
      ),
    howToFixIt: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return md(
        c.t(`Create supporting actor **{{actor}}** for this experience`, {
          actor: humanReadableActorName(g, d.actorKey)
        })
      );
    }
  },

  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL]: {
    category: 'Docusign',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return c.t(`Couldn't send Docusign envelope via email - **{{actor}}**  missing **{{addressType}}** `, {
        actor: humanReadableActorName(g, d.actorKey),
        addressType: d.addressType
          ? `${channelMarkerToString(d.addressType, c.t as UnsafeTFunction)}`
          : c.t('Email')
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL]!.summary(g),
    howToFixIt: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return md(
        c.t(`Add **{{addressType}}** to import source and run a new import, then retrigger task`, {
          addressType: d.addressType
          ? `${channelMarkerToString(d.addressType, c.t as UnsafeTFunction)}`
          : c.t('Email')
        })
      );
    }
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR]: {
    category: 'Docusign',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      // The resolver only routes here once it's confirmed the linked resource
      // is a real Docusign envelope template (see resolveInstanceSubtaskIssueKind
      // in ../types.ts) — a mismatch there routes to FAULTY_OR_UNKNOWN instead.
      const resource = d.tpl.data.resources.find((r: any) => r.resourceId === d.resourceId);
      const missingSigner = resource?.signers.find((s: any) => s.actor.key === d.actorKey);
      return c.t(`Missing supporting actor assigned as **{{docusignRole}}** in Docusign - **{{actor}}**`, {
        actor: humanReadableActorName(g, d.actorKey),
        docusignRole: missingSigner?.roleName || '-'
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR]!.summary(
        g
      ),
    howToFixIt: (g) => {
      const c = ctxOf(g);
      const inst = dataOf(g).instance;
      if (!inst || !c.onOpenParticipant) return null;
      return (
        <div>
          <LightMdToJsx>{c.t(`Add this supporting actor to the  `)}</LightMdToJsx>
          <Link onClick={() => c.onOpenParticipant?.(inst)}>{c.t(`Participant's experience`)}</Link>
          <LightMdToJsx>{c.t(` and retrigger the task`)}</LightMdToJsx>
        </div>
      );
    }
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE]: {
    category: 'Docusign',
    summary: (g) => {
      const d = dataOf(g);
      const resource = d.tpl.data.resources.find((r: any) => r.resourceId === d.resourceId);
      const templateName =
        resource?.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE ? resource.name : '';
      return ctxOf(g).t(`Couldn't access the Docusign template **{{templateName}}**`, { templateName });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__MISSING_TEMPLATE]!.summary(
        g
      ),
    howToFixIt: (g) =>
      md(
        ctxOf(g).t(
          `Check the template still exists in Docusign, or update the template settings in Dado`
        )
      )
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH]: {
    category: 'Docusign',
    summary: (g) => {
      const d = dataOf(g);
      const resource = d.tpl.data.resources.find((r: any) => r.resourceId === d.resourceId);
      const templateName =
        resource?.type === TemplateResourceType.DOCUSIGN_ENVELOPE_TEMPLATE ? resource.name : '';
      return ctxOf(g).t(`Role Names in Docusign template **{{ templateName }}** have changed`, {
        templateName
      });
    },
    whatCausedIt: (g) =>
      instanceSubtaskLegacyCopy[
        IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__ROLE_NAME_MISMATCH
      ]!.summary(g),
    // Shares "Please contact support" with FAULTY_OR_UNKNOWN in the JSX cascade.
    howToFixIt: (g) => md(ctxOf(g).t(`Please contact support`))
  },
  [IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__FAULTY_OR_UNKNOWN]: {
    category: 'Docusign',
    summary: (g) => ctxOf(g).t('Unknown Docusign error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown Docusign error'),
    howToFixIt: (g) => md(ctxOf(g).t(`Please contact support`))
  },

  [IssueKind.CANNOT_SEND_BACKGROUND_CHECK]: {
    category: 'Background check',
    // Delegates entirely to the existing BackgroundCheckInstanceProblems
    // module — its own internal branching over BackgroundCheckErrorType is
    // untouched by this refactor.
    summary: (g) => getBackgroundCheckDetail(g),
    whatCausedIt: (g) => getBackgroundCheckDetail(g),
    howToFixIt: (g) => BackgroundCheckSuggestedAction(g)
  },

  [IssueKind.SALESFORCE_CONTACT_RESOLUTION_FAILED]: {
    category: 'Salesforce',
    summary: (g) => {
      const d = dataOf(g);
      return ctxOf(g).t(`Couldn't create the Salesforce record: **{{ message }}**`, {
        message: d.errorMessage || ctxOf(g).t('Unknown Salesforce error')
      });
    },
    whatCausedIt: (g) => instanceSubtaskLegacyCopy[IssueKind.SALESFORCE_CONTACT_RESOLUTION_FAILED]!.summary(g),
    howToFixIt: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      const msg = d.errorMessage || '';
      const requiredMatch = msg.match(/Required fields? are missing:\s*\[([^\]]+)\]/i);
      if (requiredMatch) {
        const fields = requiredMatch[1];
        return md(
          c.t(
            `Map **{{ fields }}** to a Dado field in the Salesforce destination's Required field mappings (Experience template → cog icon next to the Salesforce resource), then re-enroll the participant.`,
            { fields }
          )
        );
      }
      return md(
        c.t(
          `Check the Salesforce destination's field mappings (Experience template → cog icon next to the Salesforce resource). Make sure required SF fields are mapped to Dado fields populated on the participant.`
        )
      );
    }
  },
  [IssueKind.SALESFORCE_WRITE_FAILED]: {
    category: 'Salesforce',
    summary: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      return c.t(`Couldn't write to Salesforce field **{{ object }}.{{ field }}**: **{{ message }}**`, {
        object: d.objectName || '—',
        field: d.fieldName || '—',
        message: d.errorMessage || c.t('Unknown Salesforce error')
      });
    },
    whatCausedIt: (g) => instanceSubtaskLegacyCopy[IssueKind.SALESFORCE_WRITE_FAILED]!.summary(g),
    howToFixIt: (g) => {
      const d = dataOf(g);
      const c = ctxOf(g);
      const msg = d.errorMessage || '';
      if (/INVALID_FIELD/i.test(msg))
        return md(
          c.t(
            `The Salesforce field no longer exists or the Connected App lacks permission. Verify the field still exists in Salesforce and that the Connected App's profile has Edit access.`
          )
        );
      if (/INSUFFICIENT_ACCESS|FIELD_INTEGRITY|REQUIRED_FIELD/i.test(msg))
        return md(
          c.t(
            `Salesforce rejected the write. Check the Connected App's field-level permissions and the field's validation rules in Salesforce.`
          )
        );
      return md(
        c.t(
          `Inspect the Salesforce field's validation rules + Connected App permissions, then retry. If the failure persists, update the subtask's data destination to a different field.`
        )
      );
    }
  },

  [IssueKind.INSTANCE_SUBTASK__UNKNOWN]: {
    category: 'Other',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: (g) => md(ctxOf(g).t('Unknown error'))
  },

  // --- COMPLETE_LMS_MODULE (Docebo). Placeholder copy — final text TBD. ---
  [IssueKind.COMPLETE_LMS_MODULE__NO_EMAIL]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('The assignee has no email address'),
    whatCausedIt: (g) => ctxOf(g).t('The assignee has no email address'),
    howToFixIt: (g) => md(ctxOf(g).t('Add an email address for the assignee and run a new import'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__NO_ACTOR]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('No user is assigned to complete this module'),
    whatCausedIt: (g) => ctxOf(g).t('No user is assigned to complete this module'),
    howToFixIt: (g) => md(ctxOf(g).t('Assign a user to this experience'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__NOT_A_DOCEBO_USER]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('No Docebo user was found for the assignee'),
    whatCausedIt: (g) => ctxOf(g).t('No Docebo user was found for the assignee'),
    howToFixIt: (g) => md(ctxOf(g).t('Create the user in Docebo, or correct their email address'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__ENROLL_FAILED]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('Could not enroll the assignee in the course'),
    whatCausedIt: (g) => ctxOf(g).t('Could not enroll the assignee in the course'),
    howToFixIt: (g) => md(ctxOf(g).t('Check the course settings in Docebo, then let the sub-task retry'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__UNIT_NOT_FOUND]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('The selected course no longer exists in Docebo'),
    whatCausedIt: (g) => ctxOf(g).t('The selected course no longer exists in Docebo'),
    howToFixIt: (g) => md(ctxOf(g).t('Pick a different course in the sub-task settings'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__INTEGRATION_BROKEN]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('The Docebo integration is not working'),
    whatCausedIt: (g) => ctxOf(g).t('The Docebo integration is not working (the connection may need to be renewed)'),
    howToFixIt: (g) => md(ctxOf(g).t('Reconnect Docebo on the Integrations page'))
  },
  [IssueKind.COMPLETE_LMS_MODULE__UNKNOWN]: {
    category: 'LMS module',
    summary: (g) => ctxOf(g).t('Unknown error'),
    whatCausedIt: (g) => ctxOf(g).t('Unknown error'),
    howToFixIt: (g) => md(ctxOf(g).t('Contact Dado Support'))
  }
};
